import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdEditNote,
  MdIosShare,
  MdSettings,
  MdPersonAddAlt1
} from 'react-icons/md';
import { useRouter } from 'next/router';
import { FaCrown } from 'react-icons/fa';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';

import useViewOrganization from '../../hooks/useViewOrganization';
import JoinButton from '../../../../../components/view-org-layout/JoinButton';

type MoreOptionsProps = {
  toggleShareModal: () => void;
  toggleMoreOptions: () => void;
  visible: boolean;
};

/**
 * TODO: Make this reusable as there's a bit too much of duplication with this component.
 * Implementation here is almost the right one.
 * Only piece missing is to let the content of it being set from the consumer instead of here.
 */
const MoreOptionsModal: React.FC<MoreOptionsProps> = ({
  toggleShareModal,
  toggleMoreOptions,
  visible
}) => {
  const { t } = useTranslation(['organizations']);
  const router = useRouter();
  const {
    data: { organization, organizationMemberships },
    joinOrganization
  } = useViewOrganization();

  const moreOptions = [
    {
      condition: ({ isUserAdmin }) => isUserAdmin,
      id: 'edit-organization',
      onClick:
        ({ orgId, router }) =>
        () => {
          router.push(`/organizations/${orgId}/edit`);
        },
      label: t('moreOptions.editDetails'),
      icon: <MdEditNote size={24} />
    },
    {
      condition: () => organizationMemberships?.length > 0,
      id: 'view-organization-membership-checkout',
      onClick:
        ({ orgId, router }) =>
        () => {
          router.push(`/organizations/${orgId}/memberships/checkout`);
        },
      label: t('moreOptions.viewMemberships'),
      icon: <FaCrown size={24} />
    },
    {
      condition: ({ isUserAdmin }) => isUserAdmin,
      id: 'manage-external-contacts',
      onClick:
        ({ orgId, router }) =>
        () => {
          router.push(`/organizations/${orgId}/external-contacts`);
        },
      label: t('moreOptions.manageContacts'),
      icon: <MdPersonAddAlt1 size={24} />
    },
    {
      condition: ({ isUserAdmin }) => isUserAdmin,
      id: 'edit-organization-settings',
      onClick:
        ({ orgId, router }) =>
        () => {
          router.push(`/organizations/${orgId}/edit/settings`);
        },
      label: t('moreOptions.orgSettings'),
      icon: <MdSettings size={24} />
    }
  ];

  const onOptionClick = (action) => () => {
    toggleMoreOptions();
    action();
  };

  return (
    <Modal
      type={ModalType.SMALL}
      onClose={toggleMoreOptions}
      autoHeight
      hideCloseButton
      visible={visible}
    >
      <div className="p-4 flex flex-col">
        <ul>
          {moreOptions.map((option) =>
            option.condition({ isUserAdmin: organization.isUserAdmin }) ? (
              <li
                key={option.id}
                className="font-normal text-base cursor-pointer py-4 flex align-center gap-2 text-neutral-600"
                onClick={onOptionClick(
                  option.onClick({
                    orgId: organization.id,
                    router
                  })
                )}
              >
                {option.icon}
                {option.label}
              </li>
            ) : null
          )}
        </ul>
        {!organization.isUserAdmin && (
          <div className="flex gap-2 flex-col pb-2">
            <Button
              type={ButtonType.Inverted}
              onClick={onOptionClick(toggleShareModal)}
              icon={<MdIosShare size={20} />}
            >
              {t('moreOptions.shareOrganization')}
            </Button>
          </div>
        )}
        <Divider size={4} />
        <div className="flex flex-col gap-2 mt-6">
          {organization.isUserAdmin ? (
            <Button
              type={ButtonType.Inverted}
              onClick={onOptionClick(toggleShareModal)}
              icon={<MdIosShare size={20} />}
            >
              {t('moreOptions.shareOrganization')}{' '}
            </Button>
          ) : (
            <JoinButton
              isUserPending={organization.isUserPending}
              isUserMember={organization.isUserMember}
              isUserAdmin={organization.isUserAdmin}
              join={joinOrganization}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MoreOptionsModal;
